import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, InputComponent } from '@purolator/ui-kit-angular';
import { TranslationService, TranslatePipe } from '../../../../core/i18n';
import { blurAddressField, getAddressFieldErrorMessage } from '../../utils/address-form-errors';
import { DisableBrowserAutofillDirective } from '../../../../shared/directives/disable-browser-autofill.directive';

/**
 * Expandable Address Line 2 and Address Line 3 fields shared by sender, receiver, and return sections.
 * Uses display:contents so fields participate in the parent address grid.
 */
@Component({
  selector: 'app-optional-address-lines',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    TranslatePipe,
    DisableBrowserAutofillDirective,
  ],
  templateUrl: './optional-address-lines.html',
  styleUrl: './optional-address-lines.scss',
})
export class OptionalAddressLines {
  private readonly translation = inject(TranslationService);

  /** Bumped on blur so inline error bindings refresh after markAsTouched. */
  private readonly fieldInteractionVersion = signal(0);

  readonly formGroup = input.required<FormGroup>();
  readonly expanded = input(false);
  readonly submitted = input(false);
  /** When true, Address Line 2 uses the rural/PO Box physical address label. */
  readonly ruralRouteDetected = input(false);
  /** Prefix for AVS-safe input names (e.g. shipFrom, returnAddress). */
  readonly avsNamePrefix = input.required<string>();
  readonly expandRequested = output<void>();

  protected readonly addressLine2LabelKey = computed(() =>
    this.ruralRouteDetected()
      ? 'createShipment.validation.ruralRoutePhysicalAddressLabel'
      : 'createShipment.fields.addressLine2',
  );

  /**
   * Builds a section-scoped input name so browsers do not autofill AVS fields heuristically.
   */
  protected avsFieldName(field: string): string {
    return `${this.avsNamePrefix()}-${field}`;
  }

  /**
   * Returns the inline validation message for an optional address line after blur or submit.
   */
  protected fieldError(field: 'addressLine2' | 'addressLine3'): string | null {
    this.translation.revision();
    this.fieldInteractionVersion();
    return getAddressFieldErrorMessage(
      this.formGroup().get(field),
      field,
      (key, params) => this.translation.translate(key, params),
      this.submitted(),
    );
  }

  /**
   * Marks an optional address line touched on blur so inline validation can display.
   */
  protected onFieldBlur(field: 'addressLine2' | 'addressLine3'): void {
    blurAddressField(this.formGroup().get(field));
    this.fieldInteractionVersion.update((version) => version + 1);
  }

  /**
   * Emits when the user expands Address Line 2 and Address Line 3.
   */
  protected onExpandAddressLines(): void {
    this.expandRequested.emit();
  }
}
